import { InputRichText } from '@dixa/lib-shared-habla';
import { Channel } from 'lib-shared-types-channels';
import { memo } from 'react';

import { CopilotAction, CopilotDropdown } from 'components/copilotDropdown';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import {
  EditorCopilotDropdownFeedback,
  EditorCopilotDropdownImproveReply,
  EditorCopilotDropdownMatchToStyleGuide,
  EditorCopilotDropdownSpellingAndGrammar,
  EditorCopilotDropdownTranslate,
  EditorCopilotDropdownTranslateToConversationLanguage,
} from './components';
import { EditorCopilotDropdownContext } from './editorCopilotDropdownContext';

interface EditorCopilotDropdownProps {
  channel: Channel;
}

export const editorCopilotActions = {
  improveReply: {
    title: 'Improve reply',
    icon: 'pen',
    commandName: 'richTextEditor:improveReply',
    content: <EditorCopilotDropdownImproveReply />,
  },
  improveReplyMatchStyleGuide: {
    title: 'Match to style guide',
    icon: 'widget-customize',
    commandName: 'richTextEditor:matchToStyleGuide',
    content: <EditorCopilotDropdownMatchToStyleGuide />,
    isDisabled: ({ channelStyleGuideMatch, currentEditorText }) => {
      if (!channelStyleGuideMatch) {
        return 'No style guide created for this channel';
      }
      if (currentEditorText?.length === 0) {
        return 'No text to match';
      }

      return false;
    },
  },
  translateToConversationLanguage: {
    title: "Quick translate to conversation's language",
    icon: 'translate',
    commandName: 'richTextEditor:translateToConversationLanguage',
    content: <EditorCopilotDropdownTranslateToConversationLanguage />,
  },
  translate: {
    title: 'Translate',
    icon: 'translate',
    commandName: 'richTextEditor:translate',
    content: <EditorCopilotDropdownTranslate />,
  },
  checkSpellingAndGrammar: {
    title: 'Fix spelling and grammar',
    commandName: 'richTextEditor:checkSpellingAndGrammar',
    icon: 'grammar-spelling',
    content: <EditorCopilotDropdownSpellingAndGrammar />,
  },
  sendFeedback: {
    title: 'Send feedback',
    icon: 'paper-plane',
    hasDividerBefore: true,
    content: <EditorCopilotDropdownFeedback />,
  },
} as const satisfies Record<string, CopilotAction>;

/** Represents available actions for the editor copilot dropdown. */
export type EditorCopilotActionName = keyof typeof editorCopilotActions;

/**
 * Represents a dropdown component designed to be integrated into a rich text
 * editor's toolbar as an addon, providing AI-powered functionalities to enhance
 * and refine the content within the editor.
 */
export const EditorCopilotDropdown = memo(function EditorCopilotDropdown({
  channel,
}: EditorCopilotDropdownProps): JSX.Element | null {
  const { hasCopilot } = useCopilotTrial();

  if (!hasCopilot) {
    return null;
  }

  return (
    <EditorCopilotDropdownContext.Provider value={{ channel }}>
      <div>
        <CopilotDropdown actions={editorCopilotActions} fixed noArrow noStyle>
          <InputRichText.ToolbarButton icon="ai" />
        </CopilotDropdown>
      </div>
    </EditorCopilotDropdownContext.Provider>
  );
});
